/**
 * useShortcuts — registers global keyboard shortcuts for Mission Control.
 * Uses a two-key sequence buffer ("g n", "g d", etc.) for room navigation.
 * Returns a cleanup function on unmount automatically.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const ROOM_KEYS = {
  n: "/mission-control/now",
  d: "/mission-control/decisions",
  a: "/mission-control/agents",
  p: "/mission-control/people",
  m: "/mission-control/money",
  c: "/mission-control/compass",
  l: "/mission-control/legal",
};

export function useShortcuts({ onIAmDriving, onSearch, onShortcutsOverlay }) {
  const navigate = useNavigate();
  const bufRef = useRef(""); // holds the first key of a 2-key sequence
  const bufTimer = useRef(null);

  useEffect(() => {
    function clearBuf() {
      bufRef.current = "";
      if (bufTimer.current) clearTimeout(bufTimer.current);
    }

    function handler(e) {
      const tag = e.target?.tagName || "";
      // Don't intercept when typing in inputs/textareas
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag)) return;
      if (e.target?.isContentEditable) return;

      const key = e.key;
      const ctrl = e.ctrlKey || e.metaKey;

      // ⌘K / / → search
      if ((ctrl && key === "k") || key === "/") {
        e.preventDefault();
        onSearch?.();
        clearBuf();
        return;
      }

      // ⌘. → paper/cockpit toggle (handled in MissionControlPage via this hook's callback)
      if (ctrl && key === ".") {
        e.preventDefault();
        // Fired as a custom event so MissionControlPage can toggle cockpit mode
        window.dispatchEvent(new CustomEvent("mc:toggleCockpit"));
        clearBuf();
        return;
      }

      // ⌘⇧P → policies
      if (ctrl && e.shiftKey && key === "P") {
        e.preventDefault();
        navigate("/mission-control/policies");
        clearBuf();
        return;
      }

      // ⌘⇧! → I am driving
      if (ctrl && e.shiftKey && (key === "!" || key === "1")) {
        e.preventDefault();
        onIAmDriving?.();
        clearBuf();
        return;
      }

      // ? → shortcuts overlay
      if (key === "?" && !ctrl) {
        e.preventDefault();
        onShortcutsOverlay?.();
        clearBuf();
        return;
      }

      // Esc → close drawers (custom event)
      if (key === "Escape") {
        window.dispatchEvent(new CustomEvent("mc:closeDrawer"));
        clearBuf();
        return;
      }

      // g <key> → room navigation
      if (bufRef.current === "g") {
        const route = ROOM_KEYS[key.toLowerCase()];
        if (route) {
          e.preventDefault();
          navigate(route);
        }
        clearBuf();
        return;
      }

      // g = start of a 2-key sequence
      if (key === "g" && !ctrl) {
        bufRef.current = "g";
        if (bufTimer.current) clearTimeout(bufTimer.current);
        bufTimer.current = setTimeout(clearBuf, 1500);
        return;
      }
    }

    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
      if (bufTimer.current) clearTimeout(bufTimer.current);
    };
  }, [navigate, onIAmDriving, onSearch, onShortcutsOverlay]);
}
